import { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { NavLink } from 'react-router-dom';
import client from '../../../lib/feathers';
import ProfileSurveyModal from './ProfileSurveyModal';

/**
 * What still needs the winery — the first thing they should see.
 *
 * Built on CONFIRMATION, not on completeness. On production the average listing
 * scores 86.3/100 and 1,406 of 3,096 score exactly 100 with no owner
 * involvement, so a gap-filling checklist is empty for nearly half of them —
 * which is the best explanation for why only one claimed winery has ever made an
 * edit. There was nothing to ask.
 *
 * The count is a COUNT, never a percentage: "3 of 9 confirmed" is a tally of
 * what THEY have done, where "86%" is mostly a measure of how well we crawled.
 */

const STATE_STYLE = {
  confirmed: { dot: 'bg-green-500', text: 'text-gray-400 line-through' },
  unconfirmed: { dot: 'bg-amber-400', text: 'text-gray-800' },
  missing: { dot: 'bg-gray-300', text: 'text-gray-800' },
};

// Where each item is actually done. Categories and amenities open the survey;
// the rest go to an edit form. Sections that have been split out get their own
// focused form — asking someone to check their phone number should not open a
// page covering everything they own.
const SURVEY_KEYS = new Set(['categories', 'amenities']);

const SECTION_ROUTE = {
  contactPhone: 'edit/contact',
  contactWebsite: 'edit/contact',
  socials: 'edit/contact',
  hours: 'edit/hours',
  logo: 'edit/profile',
  address: 'edit/address',
  location: 'edit/address',
};

// `categories` stays with the survey: on a first visit the activity questions
// and the amenities belong together, and the survey asks both. `amenities` has
// its own form as well, reached from the Accommodations panel, for when someone
// just wants to add "dog friendly" later.

const editPathFor = (listingId, key) =>
  `/industry/listings/${listingId}/${SECTION_ROUTE[key] || 'edit'}`;

export default function OnboardingChecklistPanel() {
  const listing = useSelector((s) => s.listings.selected);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [surveyOpen, setSurveyOpen] = useState(false);
  const [confirming, setConfirming] = useState(null);

  const load = useCallback(async () => {
    if (!listing?.id) return;
    setLoading(true);
    try {
      const res = await client.service('industry/listing-checklist').get(listing.id);
      setData(res);
    } catch (e) {
      console.log('checklist load failed', e);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [listing?.id]);

  useEffect(() => { load(); }, [load]);

  // "That's already right" has to be a real answer. For a well-crawled listing
  // most fields need no change at all, so without this the only way to clear the
  // list would be to edit things that were already correct.
  // A confirmation is a claim that the winery vouched for this, so it has to be
  // withdrawable — one tap should not record that permanently.
  const unconfirmField = async (field) => {
    setConfirming(field);
    try {
      await client.service('industry/listing-field-confirmation').remove(field, {
        query: { listingId: listing.id },
      });
      await load();
    } catch (e) {
      console.log('undo failed', e);
    } finally {
      setConfirming(null);
    }
  };

  const confirmField = async (field) => {
    setConfirming(field);
    try {
      await client.service('industry/listing-field-confirmation').create({
        listingId: listing.id, field,
      });
      await load();
    } catch (e) {
      console.log('confirm failed', e);
    } finally {
      setConfirming(null);
    }
  };

  if (loading || !data) return null;

  const { items, confirmedCount, total, nextUp } = data;
  const allDone = confirmedCount === total;
  // Nothing has ever been confirmed on this listing, so this is very likely the
  // first time anyone from the winery has looked at it. Worth saying hello and
  // explaining where the page came from, rather than opening with a task list.
  // No extra state to store: the moment they confirm anything this stops being
  // true, which is exactly when the welcome should stop appearing.
  const firstRun = confirmedCount === 0;

  return (
    <div className="bg-white shadow overflow-hidden sm:rounded-lg">
      <div className="px-4 py-5 sm:px-6 flex items-start justify-between gap-4">
        <div>
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            {firstRun
              ? `Welcome${listing?.name ? `, ${listing.name}` : ''}`
              : allDone ? 'Your listing is confirmed' : 'Help visitors find you'}
          </h3>
          <p className="mt-1 text-sm text-gray-500 max-w-2xl">
            {firstRun
              ? 'Your winery is already on VinoSeeker — people are finding you today. We built this page from your website, so some of it will be wrong. Putting it right takes about a minute, and you know things about your winery that no website says.'
              : allDone
                ? 'Everything here has been checked by you. Thank you — it makes a real difference to how you show up.'
                : 'We filled in what we could find. Tell us what we got wrong.'}
          </p>
        </div>
        <span className="shrink-0 inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800">
          {confirmedCount} of {total} confirmed
        </span>
      </div>

      {nextUp && (
        <div className="mx-4 sm:mx-6 mb-5 p-4 rounded-md bg-red-50 border border-red-100 flex items-center justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-wide text-red-800 font-semibold">
              {firstRun ? 'Start here' : 'Next'}
            </div>
            <div className="mt-0.5 text-sm text-gray-900">{nextUp.prompt}</div>
            {/* Without this the card asks "is this address correct?" and shows
                no address — a question nobody can answer where they stand. */}
            {nextUp.value && (
              <div className="mt-1 text-sm text-gray-700 font-medium">{nextUp.value}</div>
            )}
          </div>
          {SURVEY_KEYS.has(nextUp.key) ? (
            <button
              type="button"
              onClick={() => setSurveyOpen(true)}
              className="shrink-0 px-4 py-2 text-sm rounded-md text-white bg-red-800 hover:bg-red-900"
            >
              Answer
            </button>
          ) : nextUp.state === 'unconfirmed' ? (
            <div className="shrink-0 flex items-center gap-2">
              <button
                type="button"
                onClick={() => confirmField(nextUp.key)}
                disabled={confirming === nextUp.key}
                className="px-4 py-2 text-sm rounded-md text-white bg-red-800 hover:bg-red-900 disabled:opacity-50"
              >
                {confirming === nextUp.key ? 'Saving…' : "That's right"}
              </button>
              <NavLink
                to={editPathFor(listing.id, nextUp.key)}
                className="px-4 py-2 text-sm rounded-md border border-gray-300 text-gray-700 bg-white hover:bg-gray-50"
              >
                Change it
              </NavLink>
            </div>
          ) : (
            <NavLink
              to={editPathFor(listing.id, nextUp.key)}
              className="shrink-0 px-4 py-2 text-sm rounded-md text-white bg-red-800 hover:bg-red-900"
            >
              Add it
            </NavLink>
          )}
        </div>
      )}

      <div className="border-t border-gray-200 px-4 py-4 sm:px-6">
        <ul className="space-y-2.5">
          {items.map((item) => {
            const style = STATE_STYLE[item.state];
            const isSurvey = SURVEY_KEYS.has(item.key);
            return (
              <li key={item.key} className="flex items-start gap-3 text-sm">
                <span className={`w-2 h-2 mt-1.5 rounded-full shrink-0 ${style.dot}`} />
                <span className={`flex-1 ${style.text}`}>
                  {item.label}
                  {item.value && (
                    <span className="block text-xs text-gray-500 font-normal">
                      {item.value}
                    </span>
                  )}
                </span>
                {item.state === 'confirmed' && !isSurvey && (
                  <button
                    type="button"
                    onClick={() => unconfirmField(item.key)}
                    disabled={confirming === item.key}
                    className="text-xs text-gray-400 hover:underline shrink-0 disabled:opacity-50"
                  >
                    {confirming === item.key ? 'Saving…' : 'Undo'}
                  </button>
                )}
                {item.state !== 'confirmed' && (
                  isSurvey ? (
                    <button
                      type="button"
                      onClick={() => setSurveyOpen(true)}
                      className="text-xs text-red-800 hover:underline shrink-0"
                    >
                      {item.state === 'missing' ? 'Add' : 'Answer'}
                    </button>
                  ) : item.state === 'unconfirmed' ? (
                    <span className="shrink-0 flex items-center gap-3">
                      <button
                        type="button"
                        onClick={() => confirmField(item.key)}
                        disabled={confirming === item.key}
                        className="text-xs text-red-800 hover:underline disabled:opacity-50"
                      >
                        {confirming === item.key ? 'Saving…' : "That's right"}
                      </button>
                      <NavLink
                        to={editPathFor(listing.id, item.key)}
                        className="text-xs text-gray-500 hover:underline"
                      >
                        Change
                      </NavLink>
                    </span>
                  ) : (
                    <NavLink
                      to={editPathFor(listing.id, item.key)}
                      className="text-xs text-red-800 hover:underline shrink-0"
                    >
                      Add
                    </NavLink>
                  )
                )}
              </li>
            );
          })}
        </ul>
      </div>

      {surveyOpen && (
        <ProfileSurveyModal
          listing={listing}
          onClose={() => setSurveyOpen(false)}
          onSaved={load}
        />
      )}
    </div>
  );
}
